import * as React from "react";
import { unstable_batchedUpdates } from "react-dom";

import {
  exportAuditLogs,
  fetchAuditLogs,
  type AuditLogEntry,
  type AuditLogQuery,
} from "api/auditLogs";
import { middleTruncate, notify, type SelectOption } from "Components/ui";
import { downloadBlob } from "utils/download";
import {
  DEFAULT_FILTERS,
  type ActionTypeFilter,
  type AuditLogFilterState,
  type AuditLogsData,
} from "./types";

const PAGE_SIZE = 20;
const EXPORT_LIMIT = 5000;
const SEARCH_DEBOUNCE_MS = 300;
const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// Same tokens the table has always sent as `actionContains` (matched case-insensitively).
const ACTION_TOKENS: Record<Exclude<ActionTypeFilter, "all">, string> = {
  create: "CREATE,ADD",
  update: "UPDATE,EDIT,CHANGE",
  delete: "DELETE,REMOVE",
};

type FilterCriteria = Omit<AuditLogFilterState, "search">;

const pad = (value: number): string => String(value).padStart(2, "0");

/** Local calendar day as a native date-input value ("YYYY-MM-DD"). */
export const toDateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** "YYYY-MM-DD" → ISO instant at the start (or end) of that local day. */
const localDayBound = (value: string, endOfDay: boolean): string | undefined => {
  const match = DATE_INPUT_PATTERN.exec(value);
  if (!match) {
    return undefined;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const date = endOfDay
    ? new Date(year, month, day, 23, 59, 59, 999)
    : new Date(year, month, day, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const resolveDateRange = (
  criteria: FilterCriteria,
  now: Date = new Date()
): { startDate?: string; endDate?: string } => {
  if (criteria.range === "custom") {
    return {
      startDate: localDayBound(criteria.from, false),
      endDate: localDayBound(criteria.to, true),
    };
  }
  const rangeDays = Number.parseInt(criteria.range, 10);
  if (!Number.isFinite(rangeDays) || rangeDays <= 0) {
    return { startDate: undefined, endDate: undefined };
  }
  return {
    startDate: new Date(now.getTime() - rangeDays * DAY_MS).toISOString(),
    endDate: now.toISOString(),
  };
};

/** Filter part of a request, shared by the list and the CSV export. */
const buildFilterQuery = (
  base: AuditLogQuery,
  criteria: FilterCriteria,
  search: string
): AuditLogQuery => {
  const { startDate, endDate } = resolveDateRange(criteria);
  const query: AuditLogQuery = {
    ...base,
    search: search.trim() || undefined,
    actionContains: criteria.actionType === "all" ? undefined : ACTION_TOKENS[criteria.actionType],
    startDate,
    endDate,
  };
  if (criteria.resource !== "all") {
    query.actionPrefix = criteria.resource;
  }
  if (criteria.actor === "system") {
    query.actorType = "system";
  } else if (criteria.actor !== "all") {
    query.actorId = criteria.actor;
  }
  return query;
};

/**
 * State and API calls for the audit log list. `query` is merged into every request (the tenant
 * "Audit history" dialog passes `targetId` and `limit`); `limit` also sets the page size.
 */
export const useAuditLogsData = (query?: AuditLogQuery): AuditLogsData => {
  const queryKey = React.useMemo(() => JSON.stringify(query || {}), [query]);
  const baseQuery = React.useMemo<AuditLogQuery>(
    () => JSON.parse(queryKey) as AuditLogQuery,
    [queryKey]
  );
  const pageSize = baseQuery.limit && baseQuery.limit > 0 ? baseQuery.limit : PAGE_SIZE;

  const [filters, setFilters] = React.useState<AuditLogFilterState>(DEFAULT_FILTERS);
  const [search, setSearch] = React.useState<string>("");
  const [page, setPageState] = React.useState<number>(1);
  const [logs, setLogs] = React.useState<AuditLogEntry[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<unknown>(null);
  const [exporting, setExporting] = React.useState<boolean>(false);
  const [actors, setActors] = React.useState<Record<string, string>>({});
  const requestRef = React.useRef(0);

  const { actionType, resource, actor, range, from, to } = filters;

  // Typing waits for a pause before it queries; the page goes back to 1 with the new term.
  React.useEffect(() => {
    if (filters.search === search) {
      return undefined;
    }
    const handle = window.setTimeout(() => {
      unstable_batchedUpdates(() => {
        setSearch(filters.search);
        setPageState(1);
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [filters.search, search]);

  React.useEffect(() => {
    setPageState(1);
  }, [queryKey]);

  const loadLogs = React.useCallback(async () => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAuditLogs({
        ...buildFilterQuery(baseQuery, { actionType, resource, actor, range, from, to }, search),
        page,
        limit: pageSize,
      });
      if (requestId !== requestRef.current) {
        return;
      }
      const entries = response.data || [];
      setLogs(entries);
      setTotal(response.total || 0);
      setActors((current) => {
        let next = current;
        entries.forEach((entry) => {
          if (!entry.actorId) {
            return;
          }
          const label = entry.actorName || current[entry.actorId] || "";
          if (next[entry.actorId] !== label) {
            next = next === current ? { ...current } : next;
            next[entry.actorId] = label;
          }
        });
        return next;
      });
    } catch (err) {
      if (requestId === requestRef.current) {
        setError(err);
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, [actionType, actor, baseQuery, from, page, pageSize, range, resource, search, to]);

  React.useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  // Responses that land after unmount are dropped.
  React.useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  React.useEffect(() => {
    if (page > totalPages) {
      setPageState(totalPages);
    }
  }, [page, totalPages]);

  const setFilter = React.useCallback(
    <K extends keyof AuditLogFilterState>(key: K, value: AuditLogFilterState[K]) => {
      setFilters((current) => {
        const next: AuditLogFilterState = { ...current, [key]: value };
        // The API applies either a resource prefix or an action type, never both.
        if (key === "resource" && value !== "all") {
          next.actionType = "all";
        }
        // Start a custom range from the window that was showing, so the list doesn't jump.
        if (key === "range" && value === "custom" && !current.from && !current.to) {
          const days = Number.parseInt(current.range, 10) || 30;
          const now = new Date();
          next.from = toDateInputValue(new Date(now.getTime() - days * DAY_MS));
          next.to = toDateInputValue(now);
        }
        return next;
      });
      if (key !== "search") {
        setPageState(1);
      }
    },
    []
  );

  const clearFilters = React.useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearch("");
    setPageState(1);
  }, []);

  const exportCsv = React.useCallback(async () => {
    setExporting(true);
    try {
      const blob = await exportAuditLogs({
        ...buildFilterQuery(
          baseQuery,
          { actionType, resource, actor, range, from, to },
          filters.search
        ),
        limit: EXPORT_LIMIT,
      });
      downloadBlob(blob, `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      notify.error(err, { fallback: "Couldn't export the audit log. Try again." });
    } finally {
      setExporting(false);
    }
  }, [actionType, actor, baseQuery, filters.search, from, range, resource, to]);

  const setPage = React.useCallback((next: number) => setPageState(Math.max(1, next)), []);
  const reload = React.useCallback(() => void loadLogs(), [loadLogs]);
  const dismissError = React.useCallback(() => setError(null), []);

  const hasActiveFilters = (Object.keys(DEFAULT_FILTERS) as Array<keyof AuditLogFilterState>).some(
    (key) => {
      if ((key === "from" || key === "to") && filters.range !== "custom") {
        return false;
      }
      return filters[key] !== DEFAULT_FILTERS[key];
    }
  );

  const actorOptions = React.useMemo<SelectOption[]>(() => {
    const seen = Object.keys(actors)
      .map((id) => ({ value: id, label: actors[id] || middleTruncate(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // "System": payments arriving from the gateway, receipts, the nightly reconcile.
    return [{ value: "all", label: "All actors" }, { value: "system", label: "System" }, ...seen];
  }, [actors]);

  return {
    logs,
    total,
    page,
    pageSize,
    totalPages,
    setPage,
    loading,
    error,
    dismissError,
    reload,
    exporting,
    exportCsv,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    actorOptions,
  };
};
